import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import {
  deleteMailAccount,
  deleteSmsAccount,
  deleteSmsTemplate,
  fetchMessageChannels,
  saveMailAccount,
  saveSmsAccount,
  saveSmsTemplate,
} from './adminApi';

const EMPTY_MAIL = {
  name: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_encryption: 'tls',
  smtp_username: '',
  smtp_password: '',
  from_address: '',
  from_name: '',
  is_active: true,
};

const EMPTY_SMS = {
  name: '',
  api_url: '',
  api_key: '',
  client_id: '',
  sender_id: '',
  entity_id: '',
  country_prefix: '91',
  is_active: true,
};

const EMPTY_TEMPLATE = {
  sms_account_id: '',
  name: '',
  dlt_template_id: '',
  body: '',
  is_active: true,
};

/**
 * Master Settings → Messaging.
 * Stores SMTP accounts, SMS gateway accounts, and DLT-approved templates.
 */
export default function MasterMessagingSettings() {
  const [ready, setReady] = useState(true);
  const [mailAccounts, setMailAccounts] = useState([]);
  const [smsAccounts, setSmsAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [mailForm, setMailForm] = useState(EMPTY_MAIL);
  const [smsForm, setSmsForm] = useState(EMPTY_SMS);
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);
  const [editingMailId, setEditingMailId] = useState(null);
  const [editingSmsId, setEditingSmsId] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  function load() {
    fetchMessageChannels()
      .then((res) => {
        setReady(res.ready !== false);
        setMailAccounts(res.mail_accounts || []);
        setSmsAccounts(res.sms_accounts || []);
        setTemplates(res.sms_templates || []);
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function accountName(id) {
    const row = smsAccounts.find((item) => String(item.id) === String(id));
    return row ? row.name : '—';
  }

  async function submitMail(event) {
    event.preventDefault();
    try {
      const res = await saveMailAccount({ ...mailForm, is_active: !!mailForm.is_active }, editingMailId);
      swal('Saved', res.message, 'success');
      setMailForm(EMPTY_MAIL);
      setEditingMailId(null);
      load();
    } catch (err) {
      swal('Error', err.message, 'error');
    }
  }

  async function submitSms(event) {
    event.preventDefault();
    try {
      const res = await saveSmsAccount({ ...smsForm, is_active: !!smsForm.is_active }, editingSmsId);
      swal('Saved', res.message, 'success');
      setSmsForm(EMPTY_SMS);
      setEditingSmsId(null);
      load();
    } catch (err) {
      swal('Error', err.message, 'error');
    }
  }

  async function submitTemplate(event) {
    event.preventDefault();
    try {
      const res = await saveSmsTemplate({
        ...templateForm,
        sms_account_id: Number(templateForm.sms_account_id),
        is_active: !!templateForm.is_active,
      }, editingTemplateId);
      swal('Saved', res.message, 'success');
      setTemplateForm(EMPTY_TEMPLATE);
      setEditingTemplateId(null);
      load();
    } catch (err) {
      swal('Error', err.message, 'error');
    }
  }

  async function removeRow(label, action) {
    const ok = await swal({ title: `Remove this ${label}?`, icon: 'warning', buttons: true });
    if (!ok) return;
    try {
      const res = await action();
      swal('Removed', res.message, 'success');
      load();
    } catch (err) {
      swal('Error', err.message, 'error');
    }
  }

  if (!ready) {
    return <p className="text-muted">Run php artisan migrate on the server before messaging accounts can be stored.</p>;
  }

  return (
    <div className="admin-panel">
      <h5>Messaging</h5>
      <p className="text-muted small">
        Save SMTP accounts, SMS gateway accounts, and the DLT-approved template text here.
        System Monitoring alerts only choose one of these. Put {'{#var#}'} or {'{{message}}'} in the template where the alert text should go.
      </p>

      <div className="row g-4">
        <div className="col-lg-5">
          <h6>Email account</h6>
          <form onSubmit={submitMail}>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input className="form-control" required value={mailForm.name} onChange={(e) => setMailForm({ ...mailForm, name: e.target.value })} />
            </div>
            <div className="row g-2">
              <div className="col-8 mb-2">
                <label className="form-label">SMTP host</label>
                <input className="form-control" required value={mailForm.smtp_host} onChange={(e) => setMailForm({ ...mailForm, smtp_host: e.target.value })} />
              </div>
              <div className="col-4 mb-2">
                <label className="form-label">Port</label>
                <input type="number" className="form-control" value={mailForm.smtp_port} onChange={(e) => setMailForm({ ...mailForm, smtp_port: Number(e.target.value) })} />
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label">Encryption</label>
              <select className="form-select" value={mailForm.smtp_encryption} onChange={(e) => setMailForm({ ...mailForm, smtp_encryption: e.target.value })}>
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Username</label>
              <input className="form-control" autoComplete="off" value={mailForm.smtp_username} onChange={(e) => setMailForm({ ...mailForm, smtp_username: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">Password</label>
              <input type="password" className="form-control" autoComplete="new-password" value={mailForm.smtp_password} placeholder={editingMailId ? 'Leave blank to keep the saved password' : ''} onChange={(e) => setMailForm({ ...mailForm, smtp_password: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">From address</label>
              <input type="email" className="form-control" required value={mailForm.from_address} onChange={(e) => setMailForm({ ...mailForm, from_address: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">From name</label>
              <input className="form-control" value={mailForm.from_name} onChange={(e) => setMailForm({ ...mailForm, from_name: e.target.value })} />
            </div>
            <label className="form-check mb-2">
              <input type="checkbox" className="form-check-input" checked={!!mailForm.is_active} onChange={(e) => setMailForm({ ...mailForm, is_active: e.target.checked })} />
              Active
            </label>
            <div>
              <button type="submit" className="btn btn-primary btn-sm">{editingMailId ? 'Update email account' : 'Add email account'}</button>
              {editingMailId && (
                <button type="button" className="btn btn-link btn-sm" onClick={() => { setEditingMailId(null); setMailForm(EMPTY_MAIL); }}>Cancel</button>
              )}
            </div>
          </form>
        </div>
        <div className="col-lg-7">
          <h6>Saved email accounts</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Server</th><th>From</th><th></th></tr></thead>
              <tbody>
                {mailAccounts.length === 0 && <tr><td colSpan={4} className="text-muted">No email accounts yet.</td></tr>}
                {mailAccounts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}{row.is_active ? '' : ' (inactive)'}</td>
                    <td>{row.smtp_host}:{row.smtp_port}</td>
                    <td>{row.from_address}</td>
                    <td className="text-nowrap">
                      <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => { setEditingMailId(row.id); setMailForm({ ...EMPTY_MAIL, ...row, smtp_password: '' }); }}>Edit</button>
                      <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeRow('email account', () => deleteMailAccount(row.id))}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <hr className="my-4" />

      <div className="row g-4">
        <div className="col-lg-5">
          <h6>SMS account</h6>
          <form onSubmit={submitSms}>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input className="form-control" required value={smsForm.name} onChange={(e) => setSmsForm({ ...smsForm, name: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">API URL</label>
              <input className="form-control" required placeholder="https://api.xpresssms.in/api/v2/SendSMS" value={smsForm.api_url} onChange={(e) => setSmsForm({ ...smsForm, api_url: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">API key</label>
              <input type="password" className="form-control" autoComplete="new-password" value={smsForm.api_key} placeholder={editingSmsId ? 'Leave blank to keep the saved key' : ''} onChange={(e) => setSmsForm({ ...smsForm, api_key: e.target.value })} />
            </div>
            <div className="row g-2">
              <div className="col-md-6 mb-2">
                <label className="form-label">Client id</label>
                <input className="form-control" value={smsForm.client_id} onChange={(e) => setSmsForm({ ...smsForm, client_id: e.target.value })} />
              </div>
              <div className="col-md-6 mb-2">
                <label className="form-label">Sender id</label>
                <input className="form-control" value={smsForm.sender_id} onChange={(e) => setSmsForm({ ...smsForm, sender_id: e.target.value })} />
              </div>
              <div className="col-md-8 mb-2">
                <label className="form-label">Principal entity id</label>
                <input className="form-control" value={smsForm.entity_id} onChange={(e) => setSmsForm({ ...smsForm, entity_id: e.target.value })} />
              </div>
              <div className="col-md-4 mb-2">
                <label className="form-label">Country prefix</label>
                <input className="form-control" value={smsForm.country_prefix} onChange={(e) => setSmsForm({ ...smsForm, country_prefix: e.target.value })} />
              </div>
            </div>
            <label className="form-check mb-2">
              <input type="checkbox" className="form-check-input" checked={!!smsForm.is_active} onChange={(e) => setSmsForm({ ...smsForm, is_active: e.target.checked })} />
              Active
            </label>
            <div>
              <button type="submit" className="btn btn-primary btn-sm">{editingSmsId ? 'Update SMS account' : 'Add SMS account'}</button>
              {editingSmsId && (
                <button type="button" className="btn btn-link btn-sm" onClick={() => { setEditingSmsId(null); setSmsForm(EMPTY_SMS); }}>Cancel</button>
              )}
            </div>
          </form>
        </div>
        <div className="col-lg-7">
          <h6>Saved SMS accounts</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Sender</th><th>API</th><th></th></tr></thead>
              <tbody>
                {smsAccounts.length === 0 && <tr><td colSpan={4} className="text-muted">No SMS accounts yet.</td></tr>}
                {smsAccounts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}{row.is_active ? '' : ' (inactive)'}</td>
                    <td>{row.sender_id || '—'}</td>
                    <td>{row.api_url}</td>
                    <td className="text-nowrap">
                      <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => { setEditingSmsId(row.id); setSmsForm({ ...EMPTY_SMS, ...row, api_key: '' }); }}>Edit</button>
                      <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeRow('SMS account', () => deleteSmsAccount(row.id))}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <hr className="my-4" />

      <div className="row g-4">
        <div className="col-lg-5">
          <h6>Approved template</h6>
          <form onSubmit={submitTemplate}>
            <div className="mb-2">
              <label className="form-label">SMS account</label>
              <select className="form-select" required value={templateForm.sms_account_id} onChange={(e) => setTemplateForm({ ...templateForm, sms_account_id: e.target.value })}>
                <option value="">Select an account</option>
                {smsAccounts.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Name</label>
              <input className="form-control" required value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">DLT template id</label>
              <input className="form-control" required value={templateForm.dlt_template_id} onChange={(e) => setTemplateForm({ ...templateForm, dlt_template_id: e.target.value })} />
            </div>
            <div className="mb-2">
              <label className="form-label">Approved text</label>
              <textarea className="form-control" rows={3} required value={templateForm.body} placeholder="Petrosmart alert: {#var#}. ENSJ Technologies Private Limited." onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })} />
            </div>
            <label className="form-check mb-2">
              <input type="checkbox" className="form-check-input" checked={!!templateForm.is_active} onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })} />
              Active
            </label>
            <div>
              <button type="submit" className="btn btn-primary btn-sm">{editingTemplateId ? 'Update template' : 'Add template'}</button>
              {editingTemplateId && (
                <button type="button" className="btn btn-link btn-sm" onClick={() => { setEditingTemplateId(null); setTemplateForm(EMPTY_TEMPLATE); }}>Cancel</button>
              )}
            </div>
          </form>
        </div>
        <div className="col-lg-7">
          <h6>Saved templates</h6>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead><tr><th>Name</th><th>Account</th><th>Template id</th><th></th></tr></thead>
              <tbody>
                {templates.length === 0 && <tr><td colSpan={4} className="text-muted">No templates yet.</td></tr>}
                {templates.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}{row.is_active ? '' : ' (inactive)'}</td>
                    <td>{accountName(row.sms_account_id)}</td>
                    <td>{row.dlt_template_id}</td>
                    <td className="text-nowrap">
                      <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => { setEditingTemplateId(row.id); setTemplateForm({ ...EMPTY_TEMPLATE, ...row, sms_account_id: String(row.sms_account_id) }); }}>Edit</button>
                      <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeRow('template', () => deleteSmsTemplate(row.id))}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
